import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  ReactElement,
  useMemo,
  forwardRef,
} from "react";
import { useCombinedRefs } from "../utils/useCombinedRefs";
import classNames from "classnames";
import uniqueId from "lodash.uniqueid";
import { StepIndicatorProps, StepIndicatorType } from "./step-indicator.types";
import MeasureHelper from "./MeasureHelper";
import useResizeObserver from "../utils/useResizeObserver";
import {
  showSSRHydrationWarning,
  isPropEmpty,
} from "../utils/SSR/showSSRHydrationWarning";
import { useSSR } from "../utils/SSR/useSSR";

export const StepIndicator = forwardRef<HTMLDivElement, StepIndicatorProps>(
  (
    {
      activeStep,
      className,
      children,
      completedSteps = [],
      completedAriaLabel = "Completed Step",
      currentAriaLabel = "Current Step",
      dropdownHeaderText = "Select a Step",
      id,
      onChange,
      testId,
      type = StepIndicatorType.STANDARD,
    },
    containerExternalRef
  ): ReactElement => {
    const [isHeaderExpanded, updateIsHeaderExpanded] = useState<boolean>(false);
    const [isCondensed, setIsCondensed] = useState<boolean>(false);
    const [isDropdown, setIsDropdown] = useState<boolean>(false);
    const [noLineItems, setNoLineItems] = useState<number[]>([]);

    const stepItemsRef = useRef<HTMLAnchorElement[]>([]);
    const listRef = useRef<HTMLDivElement>(null);
    const dropdownHeaderRef = useRef<HTMLAnchorElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    useCombinedRefs(containerRef, containerExternalRef);

    const { current: uniqueStepIndicatorId } = useRef(
      uniqueId("step-indicator_")
    );
    useSSR(
      () => isPropEmpty(id) && showSSRHydrationWarning("id", "StepIndicator")
    );
    const stepIndicatorId = id || uniqueStepIndicatorId;

    const totalStepCount = useMemo(
      () =>
        React.Children.toArray(children).filter((child) => {
          if (!React.isValidElement(child)) {
            return null;
          }
          const childType: any = child.type;

          return childType.displayName === "Step"
            ? true
            : console.error(
                `Step Indicator only accepts a child node of type '<Step />'`
              );
        })?.length,
      [children]
    );

    useEffect(() => {
      if (totalStepCount < 3) {
        console.error(
          `Step Indicator must have at least 3 child nodes, only ${totalStepCount} were provided instead`
        );
      }
    }, [totalStepCount]);

    // Compare siblings top position and add CSS horizontal style lines between Steps
    // depending on if responsive Steps are stacked into rows (leave off lines at end of rows)
    useResizeObserver<HTMLDivElement>({
      ref: listRef as React.RefObject<HTMLDivElement>,
      onResize: () => {
        if (
          !listRef.current ||
          type === StepIndicatorType.DROPDOWN ||
          type === StepIndicatorType.STANDARD_DROPDOWN ||
          (type === StepIndicatorType.STANDARD && !isCondensed)
        ) {
          return;
        }

        const _noLineItems: number[] = [];
        let prevItem: DOMRect | undefined = undefined;
        let currItem: DOMRect;
        const items = stepItemsRef.current;
        items.forEach((item, i) => {
          currItem = item.getBoundingClientRect();
          if (prevItem && prevItem.top < currItem.top) {
            _noLineItems.push(i - 1);
          }
          prevItem = currItem;
        });

        setNoLineItems(_noLineItems);
      },
    });

    useLayoutEffect(() => {
      // Throw error if activeStep prop is disabled step
      const childrenArray = React.Children.toArray(children);
      if ((childrenArray[activeStep - 1] as any)?.props.disabled) {
        console.error(
          "Failed prop type: The prop `activeStep` cannot be disabled"
        );
      }

      /**
       * Listen for clicks that occur somewhere in the document, outside of the component itself
       */
      function handleClickOutside(e: MouseEvent): void {
        if (
          containerRef.current &&
          !containerRef.current?.contains(e.target as Node)
        ) {
          updateIsHeaderExpanded(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [containerRef, activeStep, children]);

    const onChangeHandler: StepIndicatorProps["onChange"] = (
      e,
      clickedStep
    ) => {
      onChange?.(e, clickedStep);
      isDropdown && updateIsHeaderExpanded(false);

      // when dropdown header is collapsed after step selection, focus dropdown header (click and "Enter" on keyboard)
      if (isDropdown) {
        dropdownHeaderRef?.current?.focus();
      }
    };

    /**
     * When Tabbing passed end of Step list, collapse and focus on Dropdown Header
     */

    const onKeyDownHandler = useCallback(
      (e: React.KeyboardEvent<HTMLAnchorElement>, item: number) => {
        // Tab through list
        if (e.key === "Tab" && isDropdown) {
          // to prevent the last element from getting blurred, prevent the default behavior so you can add focus after
          e.preventDefault();
          stepItemsRef.current[item]?.focus();
        }
        // Esc from list to bring focus to header dropdown
        if (e.key === "Escape" && isDropdown) {
          updateIsHeaderExpanded(false);

          isDropdown && dropdownHeaderRef.current?.focus();
        }
      },
      [isDropdown]
    );

    /**
     * Render correct variant according to the prop used or if the variant responsively renders different displays
     */
    useLayoutEffect(() => {
      switch (type) {
        case StepIndicatorType.CONDENSED:
          setIsCondensed(true);
          break;
        case StepIndicatorType.DROPDOWN:
          setIsDropdown(true);
          break;
      }
    }, [type]);

    /**
     * Enter key opens and closes Dropdown header
     */
    const onKeyPressHandler = (
      e: React.KeyboardEvent<HTMLAnchorElement>
    ): void => {
      if (e.key === "Enter" && isDropdown) {
        e.preventDefault();
        updateIsHeaderExpanded(!isHeaderExpanded);
      }
    };

    const indicatorBodyClasses = classNames("spark-step-indicator__body", {
      "spark-step-indicator__body--dropdown": isDropdown,
    });

    const stepListClasses = classNames("spark-step-indicator__list", {
      collapse: isDropdown && !isHeaderExpanded,
      "spark-step-indicator__list--condensed": isCondensed,
    });

    const DropDownHeader = (): ReactElement => {
      const dropDownHeaderClasses = classNames(
        "spark-step-indicator__item",
        "spark-step-indicator__item--current",
        "spark-step-indicator__item--dropdown__header",
        { expand: isHeaderExpanded }
      );
      const onHeaderClickHandler = (): void => {
        updateIsHeaderExpanded(!isHeaderExpanded);
      };

      const childrenArray = React.Children.toArray(children);
      const completed = completedSteps.indexOf(activeStep) !== -1;

      return (
        <a
          aria-expanded={isHeaderExpanded}
          aria-controls={stepIndicatorId}
          className={dropDownHeaderClasses}
          onClick={onHeaderClickHandler}
          onKeyPress={onKeyPressHandler}
          role="button"
          tabIndex={0}
          ref={dropdownHeaderRef}
        >
          <span className="spark-step-indicator__icon" aria-hidden={true}>
            {activeStep}
          </span>
          <span
            data-testid={testId}
            id={`step-${activeStep}`}
            className="spark-step-indicator__text"
          >
            <span className="spark-assistive-text">
              {completed && `${completedAriaLabel}, `}
              {currentAriaLabel}
            </span>
            {(childrenArray[activeStep - 1] as any).props.label}
          </span>
          <span className="spark-step-indicator__notice">
            {dropdownHeaderText}
          </span>
          <span className="spark-step-indicator__label--dropdown">
            {activeStep} of {totalStepCount}
          </span>
        </a>
      );
    };

    /**
     * Find active Steps from step list that are not disabled
     * @return {Array} indexes of active Steps
     */
    const activeItems = React.Children.map(children, (child, index) => {
      if (!(child as any)?.props.disabled) {
        return index;
      }
    });

    return (
      <div
        className={classNames("spark-step-indicator", className)}
        ref={containerRef}
        style={{ position: "relative" }}
      >
        <div className={indicatorBodyClasses}>
          {isDropdown && DropDownHeader()}
          <div
            id={stepIndicatorId}
            tabIndex={isDropdown ? -1 : undefined}
            className={stepListClasses}
            ref={listRef}
            style={
              type === StepIndicatorType.LARGE ||
              type === StepIndicatorType.CONDENSED ||
              (type === StepIndicatorType.STANDARD && isCondensed)
                ? { flexWrap: "wrap" }
                : { flexWrap: "nowrap" }
            }
          >
            {React.Children.toArray(children).map((child, i) => {
              if (!React.isValidElement(child)) {
                return null;
              }

              const childType: any = child.type;

              if (childType.displayName === "Step") {
                const stepNumber = (child.props as any).stepNumber || i + 1;

                return React.cloneElement(child, {
                  ...(child as any).props,
                  _privateProps: {
                    active: stepNumber === activeStep,
                    completed: completedSteps.indexOf(stepNumber) !== -1,
                    completedAriaLabel,
                    condensed: isCondensed,
                    noLine: noLineItems.includes(stepNumber - 1),
                    currentAriaLabel,
                    dropdown: isDropdown,
                    expanded: isHeaderExpanded,
                    stepNumber: stepNumber,
                    lastActiveItem: activeItems?.[activeItems.length - 1],
                    isFirstActiveItem: i === 0,
                    isLastActiveItem:
                      i === activeItems?.[activeItems.length - 1],
                    onClick: onChangeHandler,
                    onKeyDownLastStep: onKeyDownHandler,
                  },
                  ref: (node: HTMLAnchorElement) => {
                    // assign node to stepItems collection
                    stepItemsRef.current[i] = node;
                    // assign node to external ref request
                    const { ref } = child as any;
                    if (ref) {
                      ref.current = node;
                    }
                  },
                });
              }

              return null;
            })}
          </div>
        </div>
        {type === StepIndicatorType.STANDARD_DROPDOWN ||
        type === StepIndicatorType.STANDARD ? (
          <MeasureHelper
            id={stepIndicatorId}
            type={type}
            totalStepCount={totalStepCount}
            stepIndicatorChildren={children}
            setIsCondensed={setIsCondensed}
            setIsDropdown={setIsDropdown}
          />
        ) : null}
      </div>
    );
  }
);

StepIndicator.displayName = "StepIndicator";
